import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from "react";

const STORAGE_KEY = "tpp.viewAs";

export type ViewAsTarget = {
  employeeId: string;
  userId: string;
  name: string;
} | null;

interface ImpersonationContextValue {
  viewAs: ViewAsTarget;
  setViewAs: (target: ViewAsTarget) => void;
  clearViewAs: () => void;
}

const ImpersonationContext = createContext<ImpersonationContextValue | undefined>(undefined);

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  const [viewAs, setViewAsState] = useState<ViewAsTarget>(() => {
    if (typeof window === "undefined") return null;
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as ViewAsTarget) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (viewAs) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(viewAs));
    else sessionStorage.removeItem(STORAGE_KEY);
  }, [viewAs]);

  const setViewAs = useCallback((target: ViewAsTarget) => setViewAsState(target), []);
  const clearViewAs = useCallback(() => setViewAsState(null), []);

  const value = useMemo(() => ({ viewAs, setViewAs, clearViewAs }), [viewAs, setViewAs, clearViewAs]);

  return <ImpersonationContext.Provider value={value}>{children}</ImpersonationContext.Provider>;
}

export function useImpersonation() {
  const ctx = useContext(ImpersonationContext);
  if (!ctx) return { viewAs: null, setViewAs: () => {}, clearViewAs: () => {} } as ImpersonationContextValue;
  return ctx;
}